import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  uploadMinecraftResource, 
  updateMinecraftResource,
  uploadMinecraftFile, 
  uploadThumbnail,
  uploadGalleryImage,
  getMinecraftResource
} from '../../firebase/minecraft';
import { MinecraftResourceType, MinecraftResource } from '../../types/minecraft';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import InputField from '../../components/ui/InputField';
import TextAreaField from '../../components/ui/TextAreaField';
import PlusIcon from '../../components/icons/PlusIcon';
import WorldShareTemplate from './templates/WorldShareTemplate';

const MinecraftEducationUpload: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('edit');
  const [isEditMode, setIsEditMode] = useState(!!editId);
  const [existingResource, setExistingResource] = useState<MinecraftResource | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState<MinecraftResourceType>('world');
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [thumbnail, setThumbnail] = useState<File | null>(null);
  const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null);
  const [version, setVersion] = useState('');
  const [minecraftVersion, setMinecraftVersion] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  
  // 월드 상세 정보 (월드 타입일 때만 사용)
  const [subtitle, setSubtitle] = useState('');
  const [overview, setOverview] = useState('');
  const [learningObjectives, setLearningObjectives] = useState<string[]>([]);
  const [learningObjectiveInput, setLearningObjectiveInput] = useState('');
  const [recommendedGrade, setRecommendedGrade] = useState('');
  const [playTime, setPlayTime] = useState('');
  const [playerMode, setPlayerMode] = useState<'싱글 플레이' | '멀티 플레이' | '싱글/멀티 지원'>('싱글/멀티 지원');
  const [prerequisites, setPrerequisites] = useState<string[]>([]);
  const [prerequisiteInput, setPrerequisiteInput] = useState('');
  const [installationSteps, setInstallationSteps] = useState<string[]>([]);
  const [installationStepInput, setInstallationStepInput] = useState('');
  const [tips, setTips] = useState<string[]>([]);
  const [tipInput, setTipInput] = useState('');
  const [teacherGuideUrl, setTeacherGuideUrl] = useState('');
  
  // 수업 아이디어
  const [lessonIdeas, setLessonIdeas] = useState<Array<{
    title: string;
    summary: string;
    duration: string;
    difficulty: '초급' | '중급' | '고급';
  }>>([]);
  
  // 미션
  const [missions, setMissions] = useState<Array<{
    step: string;
    detail: string;
    successCondition?: string;
  }>>([]);
  
  // 추가 자료 링크
  const [resourceLinks, setResourceLinks] = useState<Array<{
    title: string;
    url: string;
    description?: string;
  }>>([]);
  
  // 갤러리 이미지
  const [galleryImages, setGalleryImages] = useState<Array<{
    file: File | null;
    preview: string;
    alt: string;
    description: string;
  }>>([]);
  
  const [activeTab, setActiveTab] = useState<'basic' | 'details'>('basic');
  const [loadingResource, setLoadingResource] = useState(false);

  const formatFileSize = (bytes: number) => {
    if (!bytes) return '0 MB';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const size = Math.round((bytes / Math.pow(k, i)) * 100) / 100;
    return `${size} ${sizes[i]}`;
  };

  const buildPreviewWorldData = () => {
    const galleryData = galleryImages.length > 0
      ? galleryImages.map((img, index) => ({
          src: img.preview,
          alt: img.alt.trim() || `스크린샷 ${index + 1}`,
          description: img.description.trim() || undefined,
          type: 'image' as const
        }))
      : existingResource?.gallery || [];

    const fileSizeValue = file
      ? file.size
      : existingResource?.fileSize || 0;

    return {
      title: title.trim() || '월드 제목을 입력하세요',
      subtitle: subtitle.trim() || description.trim() || existingResource?.subtitle || '',
      tags: tags.length > 0 ? tags : (existingResource?.tags || []),
      thumbnail: thumbnailPreview || existingResource?.thumbnailUrl || '/images/minecraft/world-thumbnail-placeholder.png',
      version: version.trim() || existingResource?.version || 'v1.0',
      minecraftVersion: minecraftVersion.trim() || existingResource?.minecraftVersion || 'Education Edition',
      fileSize: formatFileSize(fileSizeValue),
      downloadUrl: existingResource?.fileUrl || '#',
      teacherGuideUrl: teacherGuideUrl.trim() || existingResource?.teacherGuideUrl,
      overview: overview.trim() || description.trim() || existingResource?.overview || '',
      learningObjectives: learningObjectives.length > 0 ? learningObjectives : (existingResource?.learningObjectives || []),
      recommendedGrade: recommendedGrade.trim() || existingResource?.recommendedGrade || '',
      playTime: playTime.trim() || existingResource?.playTime || '',
      playerMode,
      prerequisites: prerequisites.length > 0 ? prerequisites : (existingResource?.prerequisites || []),
      gallery: galleryData,
      installationSteps: installationSteps.length > 0 ? installationSteps : (existingResource?.installationSteps || []),
      lessonIdeas: lessonIdeas.length > 0 ? lessonIdeas : (existingResource?.lessonIdeas || []),
      missions: missions.length > 0 ? missions : (existingResource?.missions || []),
      tips: tips.length > 0 ? tips : (existingResource?.tips || []),
      resourceLinks: resourceLinks.length > 0 ? resourceLinks : (existingResource?.resourceLinks || [])
    };
  };

  // 편집 모드일 때 기존 데이터 로드
  useEffect(() => {
    const loadExistingResource = async () => {
      if (!editId || !currentUser) return;

      setLoadingResource(true);
      try {
        const resource = await getMinecraftResource(editId);
        if (!resource) {
          setError('리소스를 찾을 수 없습니다.');
          return;
        }

        // 작성자 확인
        if (resource.authorId !== currentUser.uid) {
          setError('수정 권한이 없습니다.');
          navigate('/minecraft-education');
          return;
        }

        setExistingResource(resource);
        
        // 기존 데이터로 폼 초기화
        setTitle(resource.title);
        setDescription(resource.description);
        setType(resource.type);
        setTags(resource.tags || []);
        setVersion(resource.version || '');
        setMinecraftVersion(resource.minecraftVersion || '');
        
        if (resource.thumbnailUrl) {
          setThumbnailPreview(resource.thumbnailUrl);
        }

        // 월드 상세 정보
        if (resource.type === 'world') {
          setSubtitle(resource.subtitle || '');
          setOverview(resource.overview || resource.description);
          setLearningObjectives(resource.learningObjectives || []);
          setRecommendedGrade(resource.recommendedGrade || '');
          setPlayTime(resource.playTime || '');
          setPlayerMode(resource.playerMode || '싱글/멀티 지원');
          setPrerequisites(resource.prerequisites || []);
          setInstallationSteps(resource.installationSteps || []);
          setTips(resource.tips || []);
          setTeacherGuideUrl(resource.teacherGuideUrl || '');
          setLessonIdeas(resource.lessonIdeas || []);
          setMissions(resource.missions || []);
          setResourceLinks(resource.resourceLinks || []);
          
          // 갤러리 이미지 (URL만 저장, File은 없음)
          if (resource.gallery && resource.gallery.length > 0) {
            setGalleryImages(resource.gallery.map((img, index) => ({
              file: null as any, // 편집 모드에서는 File 없음
              preview: img.src,
              alt: img.alt,
              description: img.description || ''
            })));
          }
        }
      } catch (error) {
        console.error('리소스 로드 오류:', error);
        setError('리소스를 불러오는 중 오류가 발생했습니다.');
      } finally {
        setLoadingResource(false);
      }
    };

    loadExistingResource();
  }, [editId, currentUser, navigate]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (selectedFile.size > 100 * 1024 * 1024) {
        setError('파일 크기는 100MB를 초과할 수 없습니다.');
        return;
      }
      setFile(selectedFile);
      setError(null);
    }
  };

  const handleThumbnailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (selectedFile.size > 5 * 1024 * 1024) {
        setError('썸네일 이미지 크기는 5MB를 초과할 수 없습니다.');
        return;
      }
      setThumbnail(selectedFile);
      const reader = new FileReader();
      reader.onloadend = () => {
        setThumbnailPreview(reader.result as string);
      };
      reader.readAsDataURL(selectedFile);
      setError(null);
    }
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      setTags([...tags, tagInput.trim()]);
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  const handleTagInputKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  // 학습 목표 추가/제거
  const handleAddLearningObjective = () => {
    if (learningObjectiveInput.trim() && !learningObjectives.includes(learningObjectiveInput.trim())) {
      setLearningObjectives([...learningObjectives, learningObjectiveInput.trim()]);
      setLearningObjectiveInput('');
    }
  };

  const handleRemoveLearningObjective = (objectiveToRemove: string) => {
    setLearningObjectives(learningObjectives.filter(obj => obj !== objectiveToRemove));
  };

  // 준비물 추가/제거
  const handleAddPrerequisite = () => {
    if (prerequisiteInput.trim() && !prerequisites.includes(prerequisiteInput.trim())) {
      setPrerequisites([...prerequisites, prerequisiteInput.trim()]);
      setPrerequisiteInput('');
    }
  };

  const handleRemovePrerequisite = (prerequisiteToRemove: string) => {
    setPrerequisites(prerequisites.filter(prereq => prereq !== prerequisiteToRemove));
  };

  // 설치 방법 추가/제거
  const handleAddInstallationStep = () => {
    if (installationStepInput.trim() && !installationSteps.includes(installationStepInput.trim())) {
      setInstallationSteps([...installationSteps, installationStepInput.trim()]);
      setInstallationStepInput('');
    }
  };

  const handleRemoveInstallationStep = (stepToRemove: string) => {
    setInstallationSteps(installationSteps.filter(step => step !== stepToRemove));
  };

  // 팁 추가/제거
  const handleAddTip = () => {
    if (tipInput.trim() && !tips.includes(tipInput.trim())) {
      setTips([...tips, tipInput.trim()]);
      setTipInput('');
    }
  };

  const handleRemoveTip = (tipToRemove: string) => {
    setTips(tips.filter(tip => tip !== tipToRemove));
  };

  // 수업 아이디어 추가
  const handleAddLessonIdea = () => {
    setLessonIdeas([...lessonIdeas, {
      title: '',
      summary: '',
      duration: '',
      difficulty: '초급'
    }]);
  };

  const handleUpdateLessonIdea = (index: number, field: string, value: string) => {
    const updated = [...lessonIdeas];
    updated[index] = { ...updated[index], [field]: value };
    setLessonIdeas(updated);
  };

  const handleRemoveLessonIdea = (index: number) => {
    setLessonIdeas(lessonIdeas.filter((_, i) => i !== index));
  };

  // 미션 추가
  const handleAddMission = () => {
    setMissions([...missions, {
      step: '',
      detail: '',
      successCondition: ''
    }]);
  };

  const handleUpdateMission = (index: number, field: string, value: string) => {
    const updated = [...missions];
    updated[index] = { ...updated[index], [field]: value };
    setMissions(updated);
  };

  const handleRemoveMission = (index: number) => {
    setMissions(missions.filter((_, i) => i !== index));
  };

  // 추가 자료 링크 추가
  const handleAddResourceLink = () => {
    setResourceLinks([...resourceLinks, {
      title: '',
      url: '',
      description: ''
    }]);
  };

  const handleUpdateResourceLink = (index: number, field: string, value: string) => {
    const updated = [...resourceLinks];
    updated[index] = { ...updated[index], [field]: value };
    setResourceLinks(updated);
  };

  const handleRemoveResourceLink = (index: number) => {
    setResourceLinks(resourceLinks.filter((_, i) => i !== index));
  };

  // 갤러리 이미지 추가
  const handleAddGalleryImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      const newImages: Array<{ file: File | null; preview: string; alt: string; description: string }> = [];
      const imagePromises: Promise<void>[] = [];
      
      Array.from(files).forEach((file) => {
        if (file.type.startsWith('image/')) {
          if (file.size > 5 * 1024 * 1024) {
            setError(`이미지 크기는 5MB를 초과할 수 없습니다: ${file.name}`);
            return;
          }
          
          const promise = new Promise<void>((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => {
              newImages.push({
                file,
                preview: reader.result as string,
                alt: '',
                description: ''
              });
              resolve();
            };
            reader.readAsDataURL(file);
          });
          
          imagePromises.push(promise);
        }
      });
      
      Promise.all(imagePromises).then(() => {
        if (newImages.length > 0) {
          setGalleryImages(prev => [...prev, ...newImages]);
        }
        setError(null);
      });
      
      // 같은 파일을 다시 선택할 수 있도록 input 리셋
      e.target.value = '';
    }
  };

  const handleRemoveGalleryImage = (index: number) => {
    setGalleryImages(galleryImages.filter((_, i) => i !== index));
  };

  const handleUpdateGalleryImage = (index: number, field: 'alt' | 'description', value: string) => {
    const updated = [...galleryImages];
    updated[index] = { ...updated[index], [field]: value };
    setGalleryImages(updated);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    console.log('Form submitted', { file, title, currentUser });
    
    if (!currentUser) {
      setError('로그인이 필요합니다.');
      return;
    }

    // 편집 모드가 아니면 파일 필수
    if (!isEditMode && !file) {
      setError('파일을 선택해주세요.');
      return;
    }

    if (!title.trim()) {
      setError('제목을 입력해주세요.');
      return;
    }

    setLoading(true);
    setError(null);
    setUploadProgress(0);

    try {
      const resourceId = isEditMode && existingResource 
        ? existingResource.id 
        : `${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
      
      console.log('Starting upload/update...', resourceId, isEditMode);
      
      let fileUrl = existingResource?.fileUrl;
      if (file) {
      setUploadProgress(20);
        fileUrl = await uploadMinecraftFile(file, resourceId);
        console.log('File uploaded:', fileUrl);
      } else if (!isEditMode) {
        setError('파일을 선택해주세요.');
        setLoading(false);
        return;
      }
      
      let thumbnailUrl: string | undefined = existingResource?.thumbnailUrl;
      if (thumbnail) {
        setUploadProgress(isEditMode ? 30 : 50);
        thumbnailUrl = await uploadThumbnail(thumbnail, resourceId);
        console.log('Thumbnail uploaded:', thumbnailUrl);
      }

      setUploadProgress(isEditMode ? 40 : 70);
      
      // 갤러리 이미지 업로드
      const galleryUrls: Array<{ src: string; alt: string; description?: string }> = [];
      if (galleryImages.length > 0) {
        for (let i = 0; i < galleryImages.length; i++) {
          const galleryImage = galleryImages[i];
          // 편집 모드에서 File이 없으면 기존 URL 사용
          if (galleryImage.file && galleryImage.file instanceof File) {
            const imageUrl = await uploadGalleryImage(galleryImage.file, resourceId, i);
            galleryUrls.push({
              src: imageUrl,
              alt: galleryImage.alt.trim() || `스크린샷 ${i + 1}`,
              description: galleryImage.description.trim() || undefined
            });
          } else if (galleryImage.preview) {
            // 기존 이미지 URL 사용
            galleryUrls.push({
              src: galleryImage.preview,
              alt: galleryImage.alt.trim() || `스크린샷 ${i + 1}`,
              description: galleryImage.description.trim() || undefined
            });
          }
          setUploadProgress((isEditMode ? 40 : 70) + (i + 1) * (30 / galleryImages.length));
        }
      }

      setUploadProgress(80);
      
      // undefined 값 제거
      const resourceData: any = {
        title: title.trim(),
        description: description.trim(),
        type,
        authorId: currentUser.uid,
        authorName: currentUser.name || '익명',
        fileUrl: fileUrl!,
        fileName: file ? file.name : (existingResource?.fileName || ''),
        fileSize: file ? file.size : (existingResource?.fileSize || 0),
        tags,
      };
      
      // thumbnailUrl이 있을 때만 추가
      if (thumbnailUrl) {
        resourceData.thumbnailUrl = thumbnailUrl;
      }
      
      // version이 있을 때만 추가
      if (version.trim()) {
        resourceData.version = version.trim();
      }
      
      // minecraftVersion이 있을 때만 추가
      if (minecraftVersion.trim()) {
        resourceData.minecraftVersion = minecraftVersion.trim();
      }

      // 갤러리 이미지 추가 (월드 타입일 때만)
      if (type === 'world' && galleryUrls.length > 0) {
        resourceData.gallery = galleryUrls;
      }

      // 월드 상세 정보 추가 (월드 타입일 때만)
      if (type === 'world') {
        if (subtitle.trim()) {
          resourceData.subtitle = subtitle.trim();
        }
        if (overview.trim()) {
          resourceData.overview = overview.trim();
        }
        if (learningObjectives.length > 0) {
          resourceData.learningObjectives = learningObjectives;
        }
        if (recommendedGrade.trim()) {
          resourceData.recommendedGrade = recommendedGrade.trim();
        }
        if (playTime.trim()) {
          resourceData.playTime = playTime.trim();
        }
        if (playerMode) {
          resourceData.playerMode = playerMode;
        }
        if (prerequisites.length > 0) {
          resourceData.prerequisites = prerequisites;
        }
        if (installationSteps.length > 0) {
          resourceData.installationSteps = installationSteps;
        }
        if (tips.length > 0) {
          resourceData.tips = tips;
        }
        if (teacherGuideUrl.trim()) {
          resourceData.teacherGuideUrl = teacherGuideUrl.trim();
        }
        // 수업 아이디어 필터링 (빈 항목 제거)
        const validLessonIdeas = lessonIdeas.filter(idea => 
          idea.title.trim() && idea.summary.trim() && idea.duration.trim()
        );
        if (validLessonIdeas.length > 0) {
          resourceData.lessonIdeas = validLessonIdeas;
        }
        // 미션 필터링 (빈 항목 제거)
        const validMissions = missions.filter(mission => 
          mission.step.trim() && mission.detail.trim()
        );
        if (validMissions.length > 0) {
          resourceData.missions = validMissions;
        }
        // 추가 자료 링크 필터링 (빈 항목 제거)
        const validResourceLinks = resourceLinks.filter(link => 
          link.title.trim() && link.url.trim()
        );
        if (validResourceLinks.length > 0) {
          resourceData.resourceLinks = validResourceLinks;
        }
      }
      
      if (isEditMode && existingResource) {
        // 업데이트 모드
        await updateMinecraftResource(resourceId, resourceData, existingResource);
        console.log('Resource updated:', resourceId);
      } else {
        // 생성 모드
        const resourceId_final = await uploadMinecraftResource(resourceData, resourceId);
        console.log('Resource saved:', resourceId_final);
      }

      setUploadProgress(100);
      setSuccess(true);
      
      setTimeout(() => {
        navigate(`/minecraft-education/${resourceId}`);
      }, 2000);
    } catch (error) {
      console.error('업로드 오류:', error);
      const errorMessage = error instanceof Error ? error.message : '업로드 중 오류가 발생했습니다.';
      setError(errorMessage);
      setLoading(false);
    }
  };

  return (
    <div style={{ 
      width: '100%',
      height: '100%',
    }}>
      {/* 헤더 */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        alignSelf: 'stretch',
        padding: '8px 0px 24px 0px'
      }}>
          <h1 style={{ 
            color: 'var(--Color-text-normal, #161616)',
            fontFamily: 'var(--Font-Typeface-Type, Pretendard)',
            fontSize: 'var(--Font-Font-size-Heading-2, 32px)',
            fontStyle: 'normal',
            fontWeight: 600,
            lineHeight: '150%',
            letterSpacing: '-0.64px',
            margin: 0
          }}>
            {isEditMode ? '✏️ 리소스 수정' : '📤 리소스 업로드'}
          </h1>
      </div>

      {/* 에러 메시지 */}
          {error && (
        <Card
          width="100%"
          padding="16px 20px"
          alignItems="flex-start"
          gap={10}
          style={{
            marginBottom: '24px',
            background: '#FFF5F5',
            border: '1px solid #FFE0E0'
          }}
        >
          <div style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div style={{
              color: '#FF3838',
              fontFamily: 'Pretendard',
              fontSize: '14px',
              fontWeight: '400',
              lineHeight: '21px'
            }}>
              {error}
            </div>
            <button
              onClick={() => setError(null)}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: '#FF3838',
                fontSize: '18px',
                padding: '0',
                width: '24px',
                height: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              ×
            </button>
          </div>
        </Card>
          )}

      {/*             성공 메시지 */}
          {success && (
        <Card
          width="100%"
          padding="16px 20px"
          alignItems="flex-start"
          gap={10}
          style={{
            marginBottom: '24px',
            background: '#F0FFF4',
            border: '1px solid #C6F6D5'
          }}
        >
          <div style={{
            color: '#2F855A',
            fontFamily: 'Pretendard',
            fontSize: '14px',
            fontWeight: '400',
            lineHeight: '21px'
          }}>
            {isEditMode ? '수정이 완료되었습니다! 상세 페이지로 이동합니다...' : '업로드가 완료되었습니다! 상세 페이지로 이동합니다...'}
          </div>
        </Card>
      )}

      {/* 로딩 중 (편집 모드일 때만) */}
      {loadingResource && (
        <Card
          width="100%"
          padding="32px 24px"
          alignItems="center"
          gap={24}
        >
          <div>리소스를 불러오는 중...</div>
        </Card>
          )}

      {/* 업로드 폼 & 프리뷰 */}
      {!loadingResource && (
      <div
        style={{
          width: '100%',
          display: type === 'world' ? 'grid' : 'block',
          gridTemplateColumns: type === 'world' ? 'minmax(0, 1fr) minmax(0, 1fr)' : undefined,
          gap: type === 'world' ? '24px' : undefined
        }}
      >
      <Card
        width="100%"
        padding="32px 24px"
        alignItems="flex-start"
        gap={24}
      >
        <form onSubmit={handleSubmit} style={{ width: '100%' }}>
          {/* 탭 메뉴 (월드 타입일 때만 표시) */}
          {type === 'world' && (
            <div style={{
              display: 'flex',
              gap: '8px',
              marginBottom: '24px',
              borderBottom: '1px solid #F0F0F0'
            }}>
              <button
                type="button"
                onClick={() => setActiveTab('basic')}
                style={{
                  padding: '12px 24px',
                  border: 'none',
                  background: 'transparent',
                  borderBottom: activeTab === 'basic' ? '2px solid #FF6F0F' : '2px solid transparent',
                  color: activeTab === 'basic' ? '#161616' : '#989898',
                  fontSize: '14px',
                  fontFamily: 'Pretendard',
                  fontWeight: activeTab === 'basic' ? '600' : '400',
                  cursor: 'pointer',
                  marginBottom: '-1px'
                }}
              >
                기본 정보
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('details')}
                style={{
                  padding: '12px 24px',
                  border: 'none',
                  background: 'transparent',
                  borderBottom: activeTab === 'details' ? '2px solid #FF6F0F' : '2px solid transparent',
                  color: activeTab === 'details' ? '#161616' : '#989898',
                  fontSize: '14px',
                  fontFamily: 'Pretendard',
                  fontWeight: activeTab === 'details' ? '600' : '400',
                  cursor: 'pointer',
                  marginBottom: '-1px'
                }}
              >
                상세 정보
              </button>
            </div>
          )}

          <div style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: '24px'
          }}>
            {/* 기본 정보 탭 */}
            {(activeTab === 'basic' || type !== 'world') && (
              <>
                {/* 리소스 타입 */}
            <div style={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px'
            }}>
              <label style={{
                fontSize: '14px',
                fontWeight: '500',
                color: '#161616',
                fontFamily: 'Pretendard'
              }}>
                리소스 타입
              </label>
              <select
                    value={type}
                    onChange={(e) => setType(e.target.value as MinecraftResourceType)}
                style={{
                  width: '100%',
                  height: '48px',
                  padding: '0 16px',
                  fontSize: '14px',
                  fontFamily: 'Pretendard',
                  color: '#161616',
                  backgroundColor: 'white',
                  border: '1.2px solid #F0F0F0',
                  borderRadius: '8px',
                  outline: 'none',
                  cursor: 'pointer'
                }}
              >
                <option value="world">월드</option>
                <option value="mod">모드</option>
                <option value="code">코드</option>
              </select>
            </div>

                {/* 제목 */}
            <InputField
                  value={title}
              onChange={setTitle}
              label="제목"
              placeholder="리소스 제목을 입력하세요"
                  required
              width="100%"
                />

                {/* 설명 */}
            <TextAreaField
                  value={description}
              onChange={setDescription}
              label="설명"
              placeholder="리소스에 대한 설명을 입력하세요"
              width="100%"
                  rows={4}
                />

                {/* 파일 업로드 */}
            <div style={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px'
            }}>
              <label style={{
                fontSize: '14px',
                fontWeight: '500',
                color: '#161616',
                fontFamily: 'Pretendard'
              }}>
                파일 {isEditMode ? '(수정하지 않으면 기존 파일 유지)' : '(최대 100MB)'}
              </label>
              <div style={{
                position: 'relative',
                width: '100%'
              }}>
                    <input
                      type="file"
                  id="file-upload"
                      onChange={handleFileChange}
                      accept={
                        type === 'world' 
                          ? '.mcworld,.zip'
                          : type === 'mod'
                          ? '.mcpack,.mcaddon,.zip'
                          : '.js,.ts,.txt'
                      }
                  style={{
                    position: 'absolute',
                    width: '0',
                    height: '0',
                    opacity: 0,
                    overflow: 'hidden',
                    zIndex: -1
                  }}
                    />
                <label
                  htmlFor="file-upload"
                  style={{
                    width: '100%',
                    height: '48px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0 16px',
                    fontSize: '14px',
                    fontFamily: 'Pretendard',
                    color: file ? '#161616' : '#989898',
                    backgroundColor: 'white',
                    border: '1.2px solid #F0F0F0',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {file ? file.name : '파일 선택'}
                </label>
              </div>
                  {file && (
                <div style={{
                  fontSize: '12px',
                  color: '#989898',
                  fontFamily: 'Pretendard',
                  marginTop: '4px'
                }}>
                      파일 크기: {(file.size / 1024 / 1024).toFixed(2)} MB
                </div>
                  )}
            </div>

                {/* 썸네일 업로드 */}
            <div style={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px'
            }}>
              <label style={{
                fontSize: '14px',
                fontWeight: '500',
                color: '#161616',
                fontFamily: 'Pretendard'
              }}>
                    썸네일 이미지 (선택사항, 최대 5MB)
              </label>
              <div style={{
                position: 'relative',
                width: '100%'
              }}>
                      <input
                        type="file"
                  id="thumbnail-upload"
                        accept="image/*"
                        onChange={handleThumbnailChange}
                  style={{
                    position: 'absolute',
                    width: '0',
                    height: '0',
                    opacity: 0,
                    overflow: 'hidden',
                    zIndex: -1
                  }}
                      />
                <label
                  htmlFor="thumbnail-upload"
                  style={{
                    width: '100%',
                    height: '48px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0 16px',
                    fontSize: '14px',
                    fontFamily: 'Pretendard',
                    color: '#161616',
                    backgroundColor: 'white',
                    border: '1.2px solid #F0F0F0',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  썸네일 선택
                </label>
              </div>
                    {thumbnailPreview && (
                <div style={{
                  width: '100%',
                  maxHeight: '200px',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  border: '1px solid #F0F0F0'
                }}>
                  <img
                    src={thumbnailPreview}
                          alt="썸네일 미리보기"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain'
                    }}
                        />
                </div>
                    )}
            </div>

                {/* 태그 */}
            <div style={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px'
            }}>
              <label style={{
                fontSize: '14px',
                fontWeight: '500',
                color: '#161616',
                fontFamily: 'Pretendard'
              }}>
                    태그
              </label>
              <div style={{
                display: 'flex',
                gap: '8px',
                marginBottom: '8px'
              }}>
                <input
                  type="text"
                      placeholder="태그 입력 후 Enter"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyPress={handleTagInputKeyPress}
                  style={{
                    flex: 1,
                    height: '40px',
                    padding: '0 12px',
                    fontSize: '13px',
                    fontFamily: 'Pretendard',
                    color: '#161616',
                    backgroundColor: 'white',
                    border: '1.2px solid #F0F0F0',
                    borderRadius: '8px',
                    outline: 'none'
                  }}
                    />
                <button
                  type="button"
                  onClick={handleAddTag}
                  style={{
                    width: '40px',
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#FF6F0F',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    color: 'white'
                  }}
                >
                  <PlusIcon width={20} height={20} fill="white" />
                </button>
              </div>
              {tags.length > 0 && (
                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '8px'
                }}>
                    {tags.map((tag, index) => (
                    <div
                        key={index}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#F5F5F5',
                        borderRadius: '16px',
                        fontSize: '13px',
                        fontFamily: 'Pretendard',
                        color: '#161616',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      <span>{tag}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveTag(tag)}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          color: '#989898',
                          fontSize: '16px',
                          padding: '0',
                          width: '16px',
                          height: '16px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        ×
                      </button>
                    </div>
                    ))}
                </div>
              )}
            </div>

                {/* 버전 정보 */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '16px'
            }}>
              <InputField
                    value={version}
                onChange={setVersion}
                label="버전"
                    placeholder="예: 1.0.0"
                width="100%"
                  />
              <InputField
                    value={minecraftVersion}
                onChange={setMinecraftVersion}
                label="마인크래프트 버전"
                    placeholder="예: 1.20.0"
                width="100%"
              />
            </div>
              </>
            )}

            {/* 상세 정보 탭 (월드 타입일 때만) */}
            {type === 'world' && activeTab === 'details' && (
              <>
                {/* 서브타이틀 */}
                <InputField
                  value={subtitle}
                  onChange={setSubtitle}
                  label="서브타이틀"
                  placeholder="간단한 소개 문구를 입력하세요"
                  width="100%"
                />

                {/* 월드 소개 */}
                <TextAreaField
                  value={overview}
                  onChange={setOverview}
                  label="월드 소개"
                  placeholder="월드에 대한 상세한 설명을 입력하세요"
                  width="100%"
                  rows={4}
                />

                {/* 학습 목표 */}
                <div style={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px'
                }}>
                  <label style={{
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#161616',
                    fontFamily: 'Pretendard'
                  }}>
                    학습 목표
                  </label>
                  <div style={{
                    display: 'flex',
                    gap: '8px',
                    marginBottom: '8px'
                  }}>
                    <input
                      type="text"
                      placeholder="학습 목표를 입력 후 Enter"
                      value={learningObjectiveInput}
                      onChange={(e) => setLearningObjectiveInput(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddLearningObjective();
                        }
                      }}
                      style={{
                        flex: 1,
                        height: '40px',
                        padding: '0 12px',
                        fontSize: '13px',
                        fontFamily: 'Pretendard',
                        color: '#161616',
                        backgroundColor: 'white',
                        border: '1.2px solid #F0F0F0',
                        borderRadius: '8px',
                        outline: 'none'
                      }}
                  />
                    <button
                      type="button"
                      onClick={handleAddLearningObjective}
                      style={{
                        width: '40px',
                        height: '40px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#FF6F0F',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        color: 'white'
                      }}
                    >
                      <PlusIcon width={20} height={20} fill="white" />
                    </button>
                  </div>
                  {learningObjectives.length > 0 && (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px'
                    }}>
                      {learningObjectives.map((objective, index) => (
                        <div
                          key={index}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: '#F5F5F5',
                            borderRadius: '16px',
                            fontSize: '13px',
                            fontFamily: 'Pretendard',
                            color: '#161616',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                          }}
                        >
                          <span>{objective}</span>
                          <button
                            type="button"
                            onClick={() => handleRemoveLearningObjective(objective)}
                            style={{
                              background: 'transparent',
                              border: 'none',
                              cursor: 'pointer',
                              color: '#989898',
                              fontSize: '16px',
                              padding: '0',
                              width: '16px',
                              height: '16px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* 추천 대상, 진행 시간, 플레이 방식 */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr 1fr',
                  gap: '16px'
                }}>
                  <InputField
                    value={recommendedGrade}
                    onChange={setRecommendedGrade}
                    label="추천 대상"
                    placeholder="예: 초등 고학년 ~ 중학생"
                    width="100%"
                  />
                  <InputField
                    value={playTime}
                    onChange={setPlayTime}
                    label="권장 진행 시간"
                    placeholder="예: 50분 (2차시 권장)"
                    width="100%"
                  />
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px'
                  }}>
                    <label style={{
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#161616',
                      fontFamily: 'Pretendard'
                    }}>
                      플레이 방식
                    </label>
                    <select
                      value={playerMode}
                      onChange={(e) => setPlayerMode(e.target.value as '싱글 플레이' | '멀티 플레이' | '싱글/멀티 지원')}
                      style={{
                        width: '100%',
                        height: '48px',
                        padding: '0 16px',
                        fontSize: '14px',
                        fontFamily: 'Pretendard',
                        color: '#161616',
                        backgroundColor: 'white',
                        border: '1.2px solid #F0F0F0',
                        borderRadius: '8px',
                        outline: 'none',
                        cursor: 'pointer'
                      }}
                    >
                      <option value="싱글 플레이">싱글 플레이</option>
                      <option value="멀티 플레이">멀티 플레이</option>
                      <option value="싱글/멀티 지원">싱글/멀티 지원</option>
                    </select>
                  </div>
                </div>

                {/* 수업 전 준비물 */}
                <div style={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px'
                }}>
                  <label style={{
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#161616',
                    fontFamily: 'Pretendard'
                  }}>
                    수업 전 준비물
                  </label>
                  <div style={{
                    display: 'flex',
                    gap: '8px',
                    marginBottom: '8px'
                  }}>
                    <input
                      type="text"
                      placeholder="준비물을 입력 후 Enter"
                      value={prerequisiteInput}
                      onChange={(e) => setPrerequisiteInput(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddPrerequisite();
                        }
                      }}
                      style={{
                        flex: 1,
                        height: '40px',
                        padding: '0 12px',
                        fontSize: '13px',
                        fontFamily: 'Pretendard',
                        color: '#161616',
                        backgroundColor: 'white',
                        border: '1.2px solid #F0F0F0',
                        borderRadius: '8px',
                        outline: 'none'
                      }}
                    />
                    <button
                      type="button"
                      onClick={handleAddPrerequisite}
                      style={{
                        width: '40px',
                        height: '40px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#FF6F0F',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        color: 'white'
                      }}
                    >
                      <PlusIcon width={20} height={20} fill="white" />
                    </button>
                  </div>
                  {prerequisites.length > 0 && (
                    <div style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '8px'
                    }}>
                      {prerequisites.map((prereq, index) => (
                        <div
                          key={index}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: '#F5F5F5',
                            borderRadius: '16px',
                            fontSize: '13px',
                            fontFamily: 'Pretendard',
                            color: '#161616',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}
                        >
                          <span>{prereq}</span>
                          <button
                            type="button"
                            onClick={() => handleRemovePrerequisite(prereq)}
                            style={{
                              background: 'transparent',
                              border: 'none',
                              cursor: 'pointer',
                              color: '#989898',
                              fontSize: '16px',
                              padding: '0',
                              width: '16px',
                              height: '16px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* 설치 방법 */}
                <div style={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px'
                }}>
                  <label style={{
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#161616',
                    fontFamily: 'Pretendard'
                  }}>
                    설치 방법
                  </label>
                  <div style={{
                    display: 'flex',
                    gap: '8px',
                    marginBottom: '8px'
                  }}>
                    <input
                      type="text"
                      placeholder="설치 단계를 입력 후 Enter"
                      value={installationStepInput}
                      onChange={(e) => setInstallationStepInput(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddInstallationStep();
                        }
                      }}
                      style={{
                        flex: 1,
                        height: '40px',
                        padding: '0 12px',
                        fontSize: '13px',
                        fontFamily: 'Pretendard',
                        color: '#161616',
                        backgroundColor: 'white',
                        border: '1.2px solid #F0F0F0',
                        borderRadius: '8px',
                        outline: 'none'
                      }}
                    />
                    <button
                      type="button"
                      onClick={handleAddInstallationStep}
                      style={{
                        width: '40px',
                        height: '40px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#FF6F0F',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        color: 'white'
                      }}
                    >
                      <PlusIcon width={20} height={20} fill="white" />
                    </button>
                  </div>
                  {installationSteps.length > 0 && (
                    <ol style={{
                      margin: 0,
                      paddingLeft: '20px',
                      color: '#4C4C4C',
                      lineHeight: 1.6
                    }}>
                      {installationSteps.map((step, index) => (
                        <li key={index} style={{
                          marginBottom: '8px',
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '8px'
                        }}>
                          <span style={{ flex: 1 }}>{step}</span>
                          <button
                            type="button"
                            onClick={() => handleRemoveInstallationStep(step)}
                            style={{
                              background: 'transparent',
                              border: 'none',
                              cursor: 'pointer',
                              color: '#989898',
                              fontSize: '16px',
                              padding: '0',
                              width: '16px',
                              height: '16px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              marginTop: '2px'
                            }}
                          >
                            ×
                          </button>
                        </li>
                      ))}
                    </ol>
                  )}
                </div>

                {/* 수업 아이디어 */}
                <div style={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <label style={{
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#161616',
                      fontFamily: 'Pretendard'
                    }}>
                      수업 아이디어
                    </label>
                    <button
                      type="button"
                      onClick={handleAddLessonIdea}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#FF6F0F',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        color: 'white',
                        fontSize: '13px',
                        fontFamily: 'Pretendard',
                        fontWeight: '500',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      <PlusIcon width={16} height={16} fill="white" />
                      추가
                    </button>
                  </div>
                  {lessonIdeas.map((idea, index) => (
                    <div
                      key={index}
                      style={{
                        border: '1px solid #F0F0F0',
                        borderRadius: '12px',
                        padding: '16px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '12px',
                        background: '#FAFBFF'
                      }}
                    >
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <span style={{
                          fontSize: '14px',
                          fontWeight: '600',
                          color: '#161616'
                        }}>
                          수업 아이디어 {index + 1}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleRemoveLessonIdea(index)}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            color: '#989898',
                            fontSize: '18px',
                            padding: '0',
                            width: '24px',
                            height: '24px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          ×
                        </button>
                      </div>
                      <InputField
                        value={idea.title}
                        onChange={(value) => handleUpdateLessonIdea(index, 'title', value)}
                        placeholder="수업 아이디어 제목"
                        width="100%"
                      />
                      <TextAreaField
                        value={idea.summary}
                        onChange={(value) => handleUpdateLessonIdea(index, 'summary', value)}
                        placeholder="수업 아이디어 요약"
                        width="100%"
                        rows={2}
                      />
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr 1fr',
                        gap: '12px'
                      }}>
                        <InputField
                          value={idea.duration}
                          onChange={(value) => handleUpdateLessonIdea(index, 'duration', value)}
                          placeholder="진행 시간 (예: 50분)"
                          width="100%"
                        />
                        <select
                          value={idea.difficulty}
                          onChange={(e) => handleUpdateLessonIdea(index, 'difficulty', e.target.value)}
                          style={{
                            width: '100%',
                            height: '48px',
                            padding: '0 16px',
                            fontSize: '14px',
                            fontFamily: 'Pretendard',
                            color: '#161616',
                            backgroundColor: 'white',
                            border: '1.2px solid #F0F0F0',
                            borderRadius: '8px',
                            outline: 'none',
                            cursor: 'pointer'
                          }}
                        >
                          <option value="초급">초급</option>
                          <option value="중급">중급</option>
                          <option value="고급">고급</option>
                        </select>
                      </div>
                    </div>
                  ))}
                </div>

                {/* 미션 구성 */}
                <div style={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <label style={{
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#161616',
                      fontFamily: 'Pretendard'
                    }}>
                      미션 구성
                    </label>
                    <button
                      type="button"
                      onClick={handleAddMission}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#FF6F0F',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        color: 'white',
                        fontSize: '13px',
                        fontFamily: 'Pretendard',
                        fontWeight: '500',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      <PlusIcon width={16} height={16} fill="white" />
                      추가
                    </button>
                  </div>
                  {missions.map((mission, index) => (
                    <div
                      key={index}
                      style={{
                        border: '1px solid #F0F0F0',
                        borderRadius: '12px',
                        padding: '16px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '12px'
                      }}
                    >
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <span style={{
                          fontSize: '14px',
                          fontWeight: '600',
                          color: '#161616'
                        }}>
                          미션 {index + 1}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleRemoveMission(index)}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            color: '#989898',
                            fontSize: '18px',
                            padding: '0',
                            width: '24px',
                            height: '24px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          ×
                        </button>
                      </div>
                      <InputField
                        value={mission.step}
                        onChange={(value) => handleUpdateMission(index, 'step', value)}
                        placeholder="STEP 1. 미션 단계"
                        width="100%"
                      />
                      <TextAreaField
                        value={mission.detail}
                        onChange={(value) => handleUpdateMission(index, 'detail', value)}
                        placeholder="미션 상세 설명"
                        width="100%"
                        rows={2}
                      />
                      <InputField
                        value={mission.successCondition || ''}
                        onChange={(value) => handleUpdateMission(index, 'successCondition', value)}
                        placeholder="성공 조건 (선택사항)"
                        width="100%"
                      />
                    </div>
                  ))}
                </div>

                {/* 진행 팁 */}
                <div style={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px'
                }}>
                  <label style={{
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#161616',
                    fontFamily: 'Pretendard'
                  }}>
                    진행 팁
                  </label>
                  <div style={{
                    display: 'flex',
                    gap: '8px',
                    marginBottom: '8px'
                  }}>
                    <input
                      type="text"
                      placeholder="팁을 입력 후 Enter"
                      value={tipInput}
                      onChange={(e) => setTipInput(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddTip();
                        }
                      }}
                      style={{
                        flex: 1,
                        height: '40px',
                        padding: '0 12px',
                        fontSize: '13px',
                        fontFamily: 'Pretendard',
                        color: '#161616',
                        backgroundColor: 'white',
                        border: '1.2px solid #F0F0F0',
                        borderRadius: '8px',
                        outline: 'none'
                      }}
                    />
                    <button
                      type="button"
                      onClick={handleAddTip}
                      style={{
                        width: '40px',
                        height: '40px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#FF6F0F',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        color: 'white'
                      }}
                    >
                      <PlusIcon width={20} height={20} fill="white" />
                    </button>
                  </div>
                  {tips.length > 0 && (
                    <ul style={{
                      margin: 0,
                      paddingLeft: '20px',
                      color: '#4C4C4C',
                      lineHeight: 1.6
                    }}>
                      {tips.map((tip, index) => (
                        <li key={index} style={{
                          marginBottom: '8px',
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '8px'
                        }}>
                          <span style={{ flex: 1 }}>{tip}</span>
                          <button
                            type="button"
                            onClick={() => handleRemoveTip(tip)}
                            style={{
                              background: 'transparent',
                              border: 'none',
                              cursor: 'pointer',
                              color: '#989898',
                              fontSize: '16px',
                              padding: '0',
                              width: '16px',
                              height: '16px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              marginTop: '2px'
                            }}
                          >
                            ×
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* 추가 자료 링크 */}
                <div style={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <label style={{
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#161616',
                      fontFamily: 'Pretendard'
                    }}>
                      추가 자료 링크
                    </label>
                    <button
                      type="button"
                      onClick={handleAddResourceLink}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#FF6F0F',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        color: 'white',
                        fontSize: '13px',
                        fontFamily: 'Pretendard',
                        fontWeight: '500',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      <PlusIcon width={16} height={16} fill="white" />
                      추가
                    </button>
                  </div>
                  {resourceLinks.map((link, index) => (
                    <div
                      key={index}
                      style={{
                        border: '1px solid #F0F0F0',
                        borderRadius: '12px',
                        padding: '16px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '12px',
                        background: '#FAFBFF'
                      }}
                    >
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <span style={{
                          fontSize: '14px',
                          fontWeight: '600',
                          color: '#161616'
                        }}>
                          링크 {index + 1}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleRemoveResourceLink(index)}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            color: '#989898',
                            fontSize: '18px',
                            padding: '0',
                            width: '24px',
                            height: '24px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          ×
                        </button>
                      </div>
                      <InputField
                        value={link.title}
                        onChange={(value) => handleUpdateResourceLink(index, 'title', value)}
                        placeholder="자료 제목"
                        width="100%"
                      />
                      <InputField
                        value={link.url}
                        onChange={(value) => handleUpdateResourceLink(index, 'url', value)}
                        placeholder="URL"
                        width="100%"
                      />
                      <InputField
                        value={link.description || ''}
                        onChange={(value) => handleUpdateResourceLink(index, 'description', value)}
                        placeholder="설명 (선택사항)"
                        width="100%"
                      />
                    </div>
                  ))}
                </div>

                {/* 교사용 가이드 URL */}
                <InputField
                  value={teacherGuideUrl}
                  onChange={setTeacherGuideUrl}
                  label="교사용 가이드 URL"
                  placeholder="https://example.com/teacher-guide.pdf"
                  width="100%"
                />

                {/* 갤러리 스크린샷 */}
                <div style={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px'
                }}>
                  <label style={{
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#161616',
                    fontFamily: 'Pretendard'
                  }}>
                    월드 스크린샷 (갤러리)
                  </label>
                  <div style={{
                    position: 'relative',
                    width: '100%'
                  }}>
                    <input
                      type="file"
                      id="gallery-upload"
                      accept="image/*"
                      multiple
                      onChange={handleAddGalleryImage}
                      style={{
                        position: 'absolute',
                        width: '0',
                        height: '0',
                        opacity: 0,
                        overflow: 'hidden',
                        zIndex: -1
                      }}
                    />
                    <label
                      htmlFor="gallery-upload"
                      style={{
                        width: '100%',
                        height: '48px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '0 16px',
                        fontSize: '14px',
                        fontFamily: 'Pretendard',
                        color: '#161616',
                        backgroundColor: 'white',
                        border: '1.2px solid #F0F0F0',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      스크린샷 이미지 추가 (여러 개 선택 가능, 최대 5MB)
                    </label>
                  </div>
                  {galleryImages.length > 0 && (
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                      gap: '16px',
                      marginTop: '8px'
                    }}>
                      {galleryImages.map((galleryImage, index) => (
                        <div
                          key={index}
                          style={{
                            border: '1px solid #F0F0F0',
                            borderRadius: '12px',
                            padding: '12px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '8px',
                            background: '#FAFBFF'
                          }}
                        >
                          <div style={{
                            position: 'relative',
                            width: '100%',
                            paddingTop: '75%', // 4:3 비율
                            borderRadius: '8px',
                            overflow: 'hidden',
                            backgroundColor: '#F5F5F5'
                          }}>
                            <img
                              src={galleryImage.preview}
                              alt={`스크린샷 ${index + 1}`}
                              style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover'
                              }}
                            />
                            <button
                              type="button"
                              onClick={() => handleRemoveGalleryImage(index)}
                              style={{
                                position: 'absolute',
                                top: '8px',
                                right: '8px',
                                width: '28px',
                                height: '28px',
                                borderRadius: '50%',
                                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                                border: 'none',
                                cursor: 'pointer',
                                color: 'white',
                                fontSize: '18px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: 0
                              }}
                            >
                              ×
                            </button>
                          </div>
                          <InputField
                            value={galleryImage.alt}
                            onChange={(value) => handleUpdateGalleryImage(index, 'alt', value)}
                            placeholder="이미지 설명 (alt)"
                            width="100%"
                          />
                          <TextAreaField
                            value={galleryImage.description}
                            onChange={(value) => handleUpdateGalleryImage(index, 'description', value)}
                            placeholder="상세 설명 (선택사항)"
                            width="100%"
                            rows={2}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}

                {/* 업로드 진행률 */}
                {loading && (
              <div style={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}>
                <div style={{
                  width: '100%',
                  height: '8px',
                  backgroundColor: '#F0F0F0',
                  borderRadius: '4px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    width: `${uploadProgress}%`,
                    height: '100%',
                    backgroundColor: '#FF6F0F',
                    transition: 'width 0.3s ease'
                  }} />
                </div>
                <div style={{
                  fontSize: '12px',
                  color: '#989898',
                  fontFamily: 'Pretendard'
                }}>
                      {uploadProgress}% 업로드 중...
                </div>
              </div>
                )}

                {/* 제출 버튼 */}
            <div style={{
              display: 'flex',
              gap: '12px',
              marginTop: '8px'
            }}>
                  <Button
                width="50%"
                height="48px"
                variant="cancel"
                    onClick={() => navigate('/minecraft-education')}
                    disabled={loading}
                  >
                    취소
                  </Button>
              <button
                    type="submit"
                disabled={loading || (!isEditMode && !file) || !title.trim()}
                style={{
                  width: '50%',
                  height: '48px',
                  backgroundColor: (loading || (!isEditMode && !file) || !title.trim()) ? '#E0E0E0' : '#FF6F0F',
                  color: (loading || (!isEditMode && !file) || !title.trim()) ? '#989898' : 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontFamily: 'Pretendard',
                  fontWeight: '600',
                  cursor: (loading || (!isEditMode && !file) || !title.trim()) ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease'
                }}
                  >
                {loading ? (isEditMode ? '수정 중...' : '업로드 중...') : (isEditMode ? '수정 완료' : '업로드')}
              </button>
            </div>
          </div>
            </form>
      </Card>
      {type === 'world' && (
        <Card
          width="100%"
          padding="0"
          alignItems="stretch"
          gap={0}
        >
          <WorldShareTemplate worldData={buildPreviewWorldData()} onDownload={() => {}} />
        </Card>
      )}
      </div>
      )}
    </div>
  );
};

export default MinecraftEducationUpload;
